import { useState, useEffect, useMemo } from "react";
import {
  Users, Search, Trash2, Pencil, Loader2, Save, Store, ShoppingBag,
  Calendar, Clock, Activity, MessageCircle, UserCheck, UserX, Eye,
  ChevronDown, ChevronUp, Phone, Mail, Hash,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

interface Props {
  profiles: any[];
  onRefresh?: () => void;
}

const roleLabels: Record<string, { label: string; color: string; icon: string }> = {
  super_admin: { label: "Super Admin", color: "bg-destructive/10 text-destructive border-destructive/20", icon: "🛡️" },
  restaurant_admin: { label: "Dono de Restaurante", color: "bg-primary/10 text-primary border-primary/20", icon: "👨‍🍳" },
  support: { label: "Suporte", color: "bg-blue-500/10 text-blue-600 border-blue-500/20", icon: "🎧" },
  manager: { label: "Gerente", color: "bg-amber-500/10 text-amber-600 border-amber-500/20", icon: "📋" },
  attendant: { label: "Atendente", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", icon: "🧑‍💼" },
  kitchen: { label: "Cozinha", color: "bg-orange-500/10 text-orange-600 border-orange-500/20", icon: "🍳" },
  customer: { label: "Cliente", color: "bg-muted text-muted-foreground border-border", icon: "👤" },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 } as const,
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4, ease: "easeOut" as const } }),
};

const AdminUsersTab = ({ profiles, onRefresh }: Props) => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [userRoles, setUserRoles] = useState<Record<string, string[]>>({});
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ full_name: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [activeSegment, setActiveSegment] = useState<"owners" | "customers">("owners");
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"name" | "date" | "activity">("date");

  useEffect(() => {
    const fetchData = async () => {
      const [rolesRes, restRes, ordersRes] = await Promise.all([
        supabase.from("user_roles").select("user_id, role"),
        supabase.from("restaurants").select("id, name, slug, owner_id, phone, is_active, created_at"),
        supabase.from("orders").select("id, customer_id, restaurant_id, created_at, status, total").order("created_at", { ascending: false }).limit(1000),
      ]);

      if (rolesRes.data) {
        const map: Record<string, string[]> = {};
        rolesRes.data.forEach((r: any) => {
          if (!map[r.user_id]) map[r.user_id] = [];
          map[r.user_id].push(r.role);
        });
        setUserRoles(map);
      }
      if (restRes.data) setRestaurants(restRes.data);
      if (ordersRes.data) setOrders(ordersRes.data);
    };
    fetchData();
  }, []);

  // Separate owners from customers
  const ownerProfiles = useMemo(() => {
    return profiles.filter(p => {
      const roles = userRoles[p.user_id] || [];
      return roles.includes("restaurant_admin") || roles.includes("super_admin") || roles.includes("support") || roles.includes("manager");
    });
  }, [profiles, userRoles]);

  const customerProfiles = useMemo(() => {
    return profiles.filter(p => {
      const roles = userRoles[p.user_id] || [];
      return roles.includes("customer") || roles.length === 0;
    }).filter(p => {
      const roles = userRoles[p.user_id] || [];
      return !roles.includes("restaurant_admin") && !roles.includes("super_admin");
    });
  }, [profiles, userRoles]);

  // Map customers to restaurants via orders
  const customerRestaurantMap = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    orders.forEach(o => {
      if (o.customer_id) {
        if (!map[o.customer_id]) map[o.customer_id] = new Set();
        map[o.customer_id].add(o.restaurant_id);
      }
    });
    return map;
  }, [orders]);

  // Customer order stats
  const customerStats = useMemo(() => {
    const map: Record<string, { orderCount: number; totalSpent: number; lastOrder: string | null }> = {};
    orders.forEach(o => {
      if (!o.customer_id) return;
      if (!map[o.customer_id]) map[o.customer_id] = { orderCount: 0, totalSpent: 0, lastOrder: null };
      map[o.customer_id].orderCount++;
      map[o.customer_id].totalSpent += o.total || 0;
      if (!map[o.customer_id].lastOrder || o.created_at > map[o.customer_id].lastOrder!) {
        map[o.customer_id].lastOrder = o.created_at;
      }
    });
    return map;
  }, [orders]);

  // Owner stats
  const ownerStats = useMemo(() => {
    const map: Record<string, { restaurantCount: number; totalOrders: number; totalRevenue: number }> = {};
    restaurants.forEach(r => {
      if (!map[r.owner_id]) map[r.owner_id] = { restaurantCount: 0, totalOrders: 0, totalRevenue: 0 };
      map[r.owner_id].restaurantCount++;
      const restOrders = orders.filter(o => o.restaurant_id === r.id);
      map[r.owner_id].totalOrders += restOrders.length;
      map[r.owner_id].totalRevenue += restOrders.reduce((s, o) => s + (o.total || 0), 0);
    });
    return map;
  }, [restaurants, orders]);

  const restaurantMap = useMemo(() => {
    const map: Record<string, any> = {};
    restaurants.forEach(r => { map[r.id] = r; });
    return map;
  }, [restaurants]);

  const getOwnerRestaurants = (userId: string) => restaurants.filter(r => r.owner_id === userId);

  const getWhatsAppLink = (phone: string) => {
    const clean = phone.replace(/\D/g, "");
    const num = clean.startsWith("55") ? clean : `55${clean}`;
    return `https://wa.me/${num}`;
  };

  const currentProfiles = activeSegment === "owners" ? ownerProfiles : customerProfiles;

  const filteredProfiles = useMemo(() => {
    let list = currentProfiles.filter(p =>
      (p.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.phone || "").includes(search)
    );

    list.sort((a, b) => {
      if (sortBy === "name") return (a.full_name || "").localeCompare(b.full_name || "");
      if (sortBy === "date") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === "activity") {
        if (activeSegment === "owners") {
          return (ownerStats[b.user_id]?.totalOrders || 0) - (ownerStats[a.user_id]?.totalOrders || 0);
        }
        return (customerStats[b.user_id]?.orderCount || 0) - (customerStats[a.user_id]?.orderCount || 0);
      }
      return 0;
    });

    return list;
  }, [currentProfiles, search, sortBy, activeSegment, ownerStats, customerStats]);

  const openEdit = (profile: any) => {
    setEditingUser(profile);
    setEditForm({
      full_name: profile.full_name || "",
      phone: profile.phone || "",
      email: "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const updates: any = {};
      if (editForm.full_name !== (editingUser.full_name || "")) updates.full_name = editForm.full_name;
      if (editForm.phone !== (editingUser.phone || "")) updates.phone = editForm.phone;
      if (editForm.email) updates.email = editForm.email;

      if (Object.keys(updates).length === 0) {
        toast({ title: "Nenhuma alteração detectada" });
        setEditingUser(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke("admin-manage-users", {
        body: { action: "update_user", user_id: editingUser.user_id, updates },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Usuário atualizado com sucesso" });
      setEditingUser(null);
      onRefresh?.();
    } catch (err: any) {
      toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile: any) => {
    setDeleting(profile.user_id);
    try {
      const { data, error } = await supabase.functions.invoke("admin-manage-users", {
        body: { action: "delete_user", user_id: profile.user_id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Usuário excluído com sucesso" });
      setConfirmDelete(null);
      onRefresh?.();
    } catch (err: any) {
      toast({ title: "Erro ao excluir", description: err.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  const formatRelative = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "Hoje";
    if (days === 1) return "Ontem";
    if (days < 30) return `${days}d atrás`;
    if (days < 365) return `${Math.floor(days / 30)}m atrás`;
    return `${Math.floor(days / 365)}a atrás`;
  };

  return (
    <div className="space-y-6">
      {/* Hero Stats */}
      <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: "Total Usuários", value: profiles.length,
            icon: Users, gradient: "from-blue-500/20 to-blue-600/5",
            iconBg: "bg-blue-500/10", iconColor: "text-blue-500",
          },
          {
            label: "Donos de Restaurante", value: ownerProfiles.length,
            icon: Store, gradient: "from-primary/20 to-primary/5",
            iconBg: "bg-primary/10", iconColor: "text-primary",
          },
          {
            label: "Clientes", value: customerProfiles.length,
            icon: ShoppingBag, gradient: "from-emerald-500/20 to-emerald-600/5",
            iconBg: "bg-emerald-500/10", iconColor: "text-emerald-500",
          },
          {
            label: "Novos (7 dias)", value: profiles.filter(p => Date.now() - new Date(p.created_at).getTime() < 7 * 86400000).length,
            icon: UserCheck, gradient: "from-amber-500/20 to-amber-600/5",
            iconBg: "bg-amber-500/10", iconColor: "text-amber-500",
          },
        ].map((stat, i) => (
          <motion.div key={stat.label} custom={i} variants={fadeUp} className={`relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br ${stat.gradient} p-5`}>
            <div className={`inline-flex items-center justify-center rounded-xl ${stat.iconBg} p-2.5 mb-3`}>
              <stat.icon className={`h-5 w-5 ${stat.iconColor}`} />
            </div>
            <p className="text-2xl font-bold tracking-tight">{stat.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Segment Tabs + Search + Sort */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Tabs value={activeSegment} onValueChange={(v) => { setActiveSegment(v as any); setExpandedUser(null); }}>
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="owners" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Store className="h-4 w-4" /> Donos ({ownerProfiles.length})
            </TabsTrigger>
            <TabsTrigger value="customers" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <ShoppingBag className="h-4 w-4" /> Clientes ({customerProfiles.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-card border-border"
            />
          </div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
            <SelectTrigger className="w-[140px] bg-card border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Mais recente</SelectItem>
              <SelectItem value="name">Nome A-Z</SelectItem>
              <SelectItem value="activity">Mais ativo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{filteredProfiles.length} {activeSegment === "owners" ? "donos" : "clientes"} encontrados</p>

      {/* User List */}
      {filteredProfiles.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-20" />
          <p className="font-medium">Nenhum usuário encontrado</p>
          <p className="text-xs mt-1">Tente ajustar os filtros de busca</p>
        </div>
      ) : (
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.03 } } }} className="space-y-2">
          {filteredProfiles.map((p, i) => {
            const roles = userRoles[p.user_id] || [];
            const isSuperAdmin = roles.includes("super_admin");
            const isExpanded = expandedUser === p.user_id;
            const ownerRests = activeSegment === "owners" ? getOwnerRestaurants(p.user_id) : [];
            const custRestIds = activeSegment === "customers" ? Array.from(customerRestaurantMap[p.user_id] || []) : [];
            const custStats = customerStats[p.user_id];
            const ownStats = ownerStats[p.user_id];

            return (
              <motion.div key={p.id} custom={i} variants={fadeUp} className="rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/30 transition-colors">
                {/* Main Row */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer"
                  onClick={() => setExpandedUser(isExpanded ? null : p.user_id)}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-lg font-bold text-primary">
                    {(p.full_name || "?")[0]?.toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate">{p.full_name || "Sem nome"}</p>
                      {roles.map(role => (
                        <Badge key={role} className={`text-[10px] border ${roleLabels[role]?.color || "bg-muted text-muted-foreground"} hidden sm:inline-flex`}>
                          {roleLabels[role]?.icon} {roleLabels[role]?.label || role}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {p.phone && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {p.phone}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {formatDate(p.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="hidden md:flex items-center gap-4">
                    {activeSegment === "owners" && ownStats && (
                      <>
                        <div className="text-center">
                          <p className="text-sm font-bold">{ownStats.restaurantCount}</p>
                          <p className="text-[10px] text-muted-foreground">Restaurantes</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold">{ownStats.totalOrders}</p>
                          <p className="text-[10px] text-muted-foreground">Pedidos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-emerald-500">R$ {ownStats.totalRevenue.toFixed(0)}</p>
                          <p className="text-[10px] text-muted-foreground">Receita</p>
                        </div>
                      </>
                    )}
                    {activeSegment === "customers" && custStats && (
                      <>
                        <div className="text-center">
                          <p className="text-sm font-bold">{custStats.orderCount}</p>
                          <p className="text-[10px] text-muted-foreground">Pedidos</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-bold text-emerald-500">R$ {custStats.totalSpent.toFixed(0)}</p>
                          <p className="text-[10px] text-muted-foreground">Gasto</p>
                        </div>
                        {custStats.lastOrder && (
                          <div className="text-center">
                            <p className="text-sm font-bold">{formatRelative(custStats.lastOrder)}</p>
                            <p className="text-[10px] text-muted-foreground">Último pedido</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {p.phone && (
                      <a
                        href={getWhatsAppLink(p.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 rounded-xl hover:bg-emerald-500/10 transition-colors"
                        title="WhatsApp"
                      >
                        <MessageCircle className="h-4 w-4 text-emerald-500" />
                      </a>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                      className="p-2 rounded-xl hover:bg-primary/10 transition-colors"
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4 text-primary" />
                    </button>
                    {!isSuperAdmin && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(p); }}
                        disabled={deleting === p.user_id}
                        className="p-2 rounded-xl hover:bg-destructive/10 transition-colors disabled:opacity-50"
                        title="Excluir"
                      >
                        {deleting === p.user_id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </button>
                    )}
                    <div className="p-1">
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-border bg-secondary/20 p-4 space-y-4">
                        {/* User Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="rounded-xl bg-card border border-border p-3">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">ID</p>
                            <p className="text-xs font-mono truncate">{p.user_id}</p>
                          </div>
                          <div className="rounded-xl bg-card border border-border p-3">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Telefone</p>
                            <p className="text-xs font-medium">{p.phone || "—"}</p>
                          </div>
                          <div className="rounded-xl bg-card border border-border p-3">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Cadastro</p>
                            <p className="text-xs font-medium">{formatDate(p.created_at)}</p>
                          </div>
                          <div className="rounded-xl bg-card border border-border p-3">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Último acesso</p>
                            <p className="text-xs font-medium">
                              {activeSegment === "customers" && custStats?.lastOrder
                                ? formatRelative(custStats.lastOrder)
                                : formatRelative(p.updated_at || p.created_at)}
                            </p>
                          </div>
                        </div>

                        {/* Roles */}
                        <div>
                          <p className="text-xs font-semibold mb-2">Papéis</p>
                          <div className="flex flex-wrap gap-1.5">
                            {roles.length === 0 ? (
                              <Badge variant="outline" className="text-xs">Sem papel atribuído</Badge>
                            ) : roles.map(role => (
                              <Badge key={role} className={`text-xs border ${roleLabels[role]?.color}`}>
                                {roleLabels[role]?.icon} {roleLabels[role]?.label || role}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Owner: Restaurant List */}
                        {activeSegment === "owners" && ownerRests.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold mb-2">Restaurantes ({ownerRests.length})</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {ownerRests.map(r => {
                                const rOrders = orders.filter(o => o.restaurant_id === r.id);
                                const rRevenue = rOrders.reduce((s, o) => s + (o.total || 0), 0);
                                return (
                                  <div key={r.id} className="flex items-center gap-3 rounded-xl bg-card border border-border p-3">
                                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                      <Store className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{r.name}</p>
                                      <p className="text-[10px] text-muted-foreground">/{r.slug}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-xs font-bold">{rOrders.length} pedidos</p>
                                      <p className="text-[10px] text-emerald-500">R$ {rRevenue.toFixed(0)}</p>
                                    </div>
                                    <Badge variant={r.is_active ? "default" : "secondary"} className="text-[10px]">
                                      {r.is_active ? "Ativo" : "Inativo"}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Customer: Associated Restaurants */}
                        {activeSegment === "customers" && custRestIds.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold mb-2">Cliente em ({custRestIds.length}) restaurante(s)</p>
                            <div className="flex flex-wrap gap-2">
                              {custRestIds.map(rid => {
                                const r = restaurantMap[rid];
                                if (!r) return null;
                                const custOrdersInRest = orders.filter(o => o.customer_id === p.user_id && o.restaurant_id === rid);
                                return (
                                  <div key={rid} className="flex items-center gap-2 rounded-xl bg-card border border-border px-3 py-2">
                                    <Store className="h-3.5 w-3.5 text-primary" />
                                    <span className="text-xs font-medium">{r.name}</span>
                                    <Badge variant="outline" className="text-[10px]">{custOrdersInRest.length} pedidos</Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Activity Summary */}
                        <div className="flex items-center gap-3">
                          <Activity className="h-4 w-4 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            {activeSegment === "owners"
                              ? `${ownStats?.totalOrders || 0} pedidos recebidos · R$ ${(ownStats?.totalRevenue || 0).toFixed(2)} em receita`
                              : `${custStats?.orderCount || 0} pedidos realizados · R$ ${(custStats?.totalSpent || 0).toFixed(2)} gastos`}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" /> Editar Usuário
            </DialogTitle>
            <DialogDescription>Altere os dados do usuário abaixo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Nome completo</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                className="bg-card border-border"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Telefone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                className="bg-card border-border"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">E-mail</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                placeholder="Novo e-mail (opcional)"
                className="bg-card border-border"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditingUser(null)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Excluir Usuário
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir permanentemente <span className="font-semibold text-foreground">{confirmDelete?.full_name || "este usuário"}</span>?
              Todos os restaurantes e dados relacionados serão removidos. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={!!deleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              disabled={!!deleting}
              className="gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Excluir Permanentemente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersTab;
